import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, User, Mail, Phone, MessageSquare, CarFront } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITIONS = [
  { value: "fremragende", label: "Fremragende" },
  { value: "god", label: "God" },
  { value: "middel", label: "Middel" },
  { value: "renoveringsobjekt", label: "Renoveringsobjekt" },
];

const FUELS = ["Benzin", "Diesel", "El", "Hybrid", "Plug-in Hybrid"];
const TRANSMISSIONS = ["Manuelt", "Automatisk"];

function Field({ icon: Icon, label, children, span2 }) {
  return (
    <label className={`group flex items-start gap-3 bg-white border border-[#D7CFC0] rounded-xl px-4 py-3 hover:border-copper focus-within:border-copper focus-within:ring-2 focus-within:ring-copper/15 transition-all cursor-text ${span2 ? "sm:col-span-2" : ""}`}>
      {Icon && <Icon className="w-4 h-4 text-graphite/55 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-graphite/55">{label}</div>
        {children}
      </div>
    </label>
  );
}

const inputCls = "w-full bg-transparent outline-none text-graphite font-medium placeholder:text-graphite/30";

export default function SellCar() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    license_plate: "", make: "", model: "", year: "", km: "",
    fuel_type: "", transmission: "", condition: "", asking_price: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit = form.name.trim() && form.email.trim() && form.phone.trim() && form.make.trim() && form.model.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/public/sell-inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        license_plate: form.license_plate.trim(),
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year ? parseInt(form.year, 10) : null,
        km: form.km ? parseInt(form.km, 10) : null,
        fuel_type: form.fuel_type,
        transmission: form.transmission,
        condition: form.condition,
        asking_price: form.asking_price ? parseFloat(form.asking_price) : null,
        message: form.message.trim(),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Noget gik galt — prøv venligst igen eller ring til os direkte.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#F7F3EC] min-h-screen">
        <section className="max-w-2xl mx-auto px-4 sm:px-6 py-24 md:py-32 text-center" data-testid="sell-confirmation">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={32} strokeWidth={1.7}/>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-graphite mb-4">Tak for din henvendelse!</h1>
          <p className="text-graphite/75 text-base md:text-lg leading-relaxed mb-8">
            Vi kigger din bil igennem og vender tilbage inden for 24 timer med et uforpligtende bud eller opfølgende spørgsmål.
          </p>
          <a href="/" className="btn-primary inline-block" data-testid="sell-back-home">Tilbage til forsiden</a>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-[#F7F3EC] min-h-screen" data-testid="sell-car-page">
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        <div className="mb-10 md:mb-14">
          <div className="label-tag mb-3">Sælg din bil</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-graphite mb-4">Vi køber din bil.</h1>
          <p className="text-graphite/75 text-base md:text-lg leading-relaxed max-w-2xl">
            Send os detaljerne om din bil, så vender vi tilbage med et fair bud inden for 24 timer. Ingen forpligtelser, ingen krav om ekstra prøvekørsel — bare et hurtigt og gennemsigtigt forløb.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="sell-car-form">
          {/* Kontakt */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-graphite/55 mb-3">Dine kontaktoplysninger</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field icon={User} label="Fulde navn *">
                <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="sell-name"/>
              </Field>
              <Field icon={Phone} label="Telefon *">
                <input required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="sell-phone"/>
              </Field>
              <Field icon={Mail} label="Email *" span2>
                <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="sell-email"/>
              </Field>
            </div>
          </div>

          {/* Bil-info */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-graphite/55 mb-3">Bilens data</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field icon={CarFront} label="Nummerplade (valgfri)">
                <input className={inputCls + " uppercase"} value={form.license_plate} onChange={set("license_plate")} placeholder="XX12345" data-testid="sell-plate"/>
              </Field>
              <Field label="Mærke *">
                <input required className={inputCls} value={form.make} onChange={set("make")} placeholder="fx Toyota" data-testid="sell-make"/>
              </Field>
              <Field label="Model *">
                <input required className={inputCls} value={form.model} onChange={set("model")} placeholder="fx Aygo" data-testid="sell-model"/>
              </Field>
              <Field label="Årgang">
                <input type="number" min="1970" max="2030" className={inputCls} value={form.year} onChange={set("year")} placeholder="fx 2018" data-testid="sell-year"/>
              </Field>
              <Field label="Kilometer">
                <input type="number" min="0" className={inputCls} value={form.km} onChange={set("km")} placeholder="fx 85000" data-testid="sell-km"/>
              </Field>
              <Field label="Brændstof">
                <select className={inputCls} value={form.fuel_type} onChange={set("fuel_type")} data-testid="sell-fuel">
                  <option value="">— Vælg —</option>
                  {FUELS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Gearkasse">
                <select className={inputCls} value={form.transmission} onChange={set("transmission")} data-testid="sell-transmission">
                  <option value="">— Vælg —</option>
                  {TRANSMISSIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Stand">
                <select className={inputCls} value={form.condition} onChange={set("condition")} data-testid="sell-condition">
                  <option value="">— Vælg —</option>
                  {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Ønsket pris (DKK)" span2>
                <input type="number" min="0" step="1000" className={inputCls} value={form.asking_price} onChange={set("asking_price")} placeholder="fx 45000" data-testid="sell-price"/>
              </Field>
            </div>
          </div>

          {/* Besked */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-graphite/55 mb-3">Yderligere info</div>
            <Field icon={MessageSquare} label="Besked (valgfrit)">
              <textarea
                rows={4}
                className={inputCls + " resize-none"}
                value={form.message}
                onChange={set("message")}
                placeholder="Servicehistorik, kendte skader, ekstraudstyr, mv."
                data-testid="sell-message"
              />
            </Field>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="sell-error">
              {error}
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="btn-primary w-full sm:w-auto sm:min-w-[280px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="sell-submit"
            >
              {submitting ? "Sender…" : "Send henvendelse →"}
            </button>
            <p className="text-xs text-graphite/55 mt-3">Vi behandler dine oplysninger fortroligt og bruger dem kun til at give dig et bud.</p>
          </div>
        </form>
      </section>
    </div>
  );
}
